import { useEffect, useMemo, useRef, useState } from "react";

const useRegistrationLoadingBar = (isLoading, authDataExisted) => {
    const [progress, setProgress] = useState(0);
    const [statusText, setStatusText] = useState("");

    // Max progress while loading — never reach 100% automatically
    const MAX_LOADING_PROGRESS = 95; // leaves room for finalization to push to 100%

    // Determine the initial baseline based on whether auth data existed
    const initialBaseline = useMemo(() => {
        if (authDataExisted === "false") return 15; // brand new user
        if (authDataExisted === "true") return 30;  // returning user with data
        return 50;                                   // unknown/mid state
    }, [authDataExisted]);

    const intervalRef = useRef(null);

    useEffect(() => {
        // Clear any previous interval when dependencies change
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }

        if (!isLoading) return;

        // Ensure we start at least at the baseline when loading starts
        setProgress(prev => (prev < initialBaseline ? initialBaseline : prev));

        // Gradual, easing increment towards MAX_LOADING_PROGRESS
        // Uses an exponential ease-out: each tick adds a fraction of the remaining distance
        intervalRef.current = setInterval(() => {
            setProgress(prev => {
                if (prev >= MAX_LOADING_PROGRESS) return prev;
                const remaining = MAX_LOADING_PROGRESS - prev;
                // Add 5% of remaining each 500ms, but ensure a small minimum step to show activity
                const increment = Math.max(0.2, remaining * 0.05);
                const next = Math.min(prev + increment, MAX_LOADING_PROGRESS);
                return Number(next.toFixed(2));
            });
        }, 500);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [isLoading, initialBaseline]);

    useEffect(() => {
        if (progress <= 20) {
            setStatusText("Wir erstellen Deinen Account...");
        } else if (progress <= 40) {
            setStatusText("Dein Benutzer wird erstellt...");
        } else if (progress <= 70) {
            setStatusText("Wir validieren Deine Anmeldedaten...");
        } else if (progress < MAX_LOADING_PROGRESS) {
            setStatusText("Wir schließen Deine Anfrage ab...");
        }
    }, [progress]);

    return {
        progress,
        setProgress,
        statusText,
    };
};

export default useRegistrationLoadingBar;